import { LiaDataElement } from '@liasoft/lit-ui';
import { html, nothing } from 'lit';
import { whoAmI } from '../api/system.js';
import { ADMIN_PATH } from '../../shared/paths.js';
import { errorPanel, loadingPanel } from '../../shared/ui/templates.js';

/**
 * What an <b>operator</b> sees at `/`.
 *
 * The developer's front page is `<user-dashboard>`, and which of the two renders is decided by which
 * credential the visitor holds - see `user/routes.js`. This is the other case: somebody signed in with a
 * portal account, on the application that is not the portal. There is nothing here for them to do,
 * because everything they came for is under `/admin`, so this says so and points at it.
 *
 * The link appears only for an account that holds the Admin role. That is <b>presentation</b>: the
 * administration API refuses everybody else regardless, and hiding the link is a convenience rather than
 * a control. `GET /api/v1/system/me` is the cheapest thing here that requires a valid credential, so it
 * doubles as the check that the token this browser is holding is still good.
 */
export class UserHome extends LiaDataElement
{
   static properties = { account: { state: true } };

   constructor()
   {
      super();
      this.account = null;
   }

   async load(signal)
   {
      this.account = await whoAmI(signal);
   }

   get administrator()
   {
      return this.account?.isAdmin === true;
   }

   render()
   {
      if (this.loading && !this.account)
      {
         return loadingPanel('Loading your account');
      }

      return html`
         <lia-page
            title="covdbg"
            icon="fa-solid fa-house"
            description="Your covdbg account."
         >
            <div class="d-flex flex-column gap-3">
               ${this.error ? errorPanel(this.error, () => this.reload()) : nothing}

               <lia-card
                  title=${`Hello, ${this.account?.userName ?? 'there'}`}
                  icon="fa-solid fa-hand"
                  .content=${html`
                     <p class="mb-2">You are signed in${this.account?.userName ? html` as <strong>${this.account.userName}</strong>` : nothing} with a portal account.</p>
                     <p class="text-body-secondary mb-0">
                        This page is the developer's - their seats, the addresses they commit under and
                        what covdbg decided about their runs. A portal account is not a developer
                        account, so there is nothing of yours to show here. Signing in with the address
                        you commit under would show you that view instead.
                     </p>`}
               ></lia-card>

               ${this.administrator
                  ? html`
                     <lia-card
                        title="Administration"
                        icon="fa-solid fa-screwdriver-wrench"
                        .content=${html`
                           <p class="mb-2">
                              This account may administer the service. The portal is a separate
                              application, so following this link loads it.
                           </p>
                           <a class="btn btn-primary btn-sm" href=${ADMIN_PATH}>
                              <i class="fa-solid fa-arrow-right me-1" aria-hidden="true"></i>
                              Open the admin portal
                           </a>`}
                     ></lia-card>`
                  : nothing}
            </div>
         </lia-page>`;
   }
}

customElements.define('user-home', UserHome);
